import React from 'react';
import Linkify from 'linkifyjs/react';
import styled from 'styled-components/macro';
import { GreenCheckIcon as GreenCircleCheckIcon, RedSquareCancelIcon, YellowCircleInfoIcon } from 'assets/icons';
import { StripePaymentMethodType as StripePaymentMethodType } from 'shared/types/graphql';
import { InvoiceStatus, useStripeTerminalProvider } from 'shared/providers/StripeTerminalProvider';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SidePanelInvoicingEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IInvoiceStatusMessage {
  status: InvoiceStatus;
  clientFirstName?: string;
  clientLastName?: string;
  invoiceTotal: string;
  terminalName: string;
  dismissCallback: () => void;
  cancelCallback?: () => void;
  retryCallback?: () => void;
  errorMessage?: string;
  isTransactionLoading: boolean;
  paymentMethodType?: StripePaymentMethodType;
}

const OKGotIt = 'Ok, got it';

const InvoiceStatusMessage = ({
  status,
  errorMessage,
  invoiceTotal,
  terminalName,
  cancelCallback,
  retryCallback,
  clientLastName,
  dismissCallback,
  clientFirstName,
  paymentMethodType,
  isTransactionLoading,
}: IInvoiceStatusMessage): JSX.Element | null => {
  const { setCurrentInvoiceStatus } = useStripeTerminalProvider();
  const { trackEvent } = useTracking();

  const formatTerminalName = (terminalName: string): string => {
    return terminalName.toLowerCase().includes('terminal') ? terminalName : terminalName + ' Terminal';
  };

  // TODO: [Rebranding] These colors will be updated in a future phase
  const configMap = {
    [InvoiceStatus.Sending]: {
      borderColor: '#edc308',
      backgroundColor: '#fcf4d3',
      actionButtonText: 'Cancel',
      icon: <YellowCircleInfoIcon />,
      title: 'Sending to Terminal...',
      message: `Sending an invoice of $${invoiceTotal} to ${formatTerminalName(
        terminalName,
      )} for ${clientFirstName} ${clientLastName}.`,
    },
    [InvoiceStatus.Pending]: {
      borderColor: '#edc308',
      backgroundColor: '#fcf4d3',
      actionButtonText: 'Cancel Invoice',
      icon: <YellowCircleInfoIcon />,
      title: 'Invoice Sent',
      message: `Sent an invoice of $${invoiceTotal} to ${formatTerminalName(
        terminalName,
      )} for ${clientFirstName} ${clientLastName}.`,
    },
    [InvoiceStatus.Canceled]: {
      borderColor: '#da4167',
      backgroundColor: '#fbeaee',
      actionButtonText: OKGotIt,
      icon: <RedSquareCancelIcon />,
      title: 'Invoice Canceled',
      message: `You just canceled an invoice of $${invoiceTotal} for ${clientFirstName} ${clientLastName}${
        paymentMethodType && paymentMethodType === StripePaymentMethodType.CardPresent
          ? ' at ' + formatTerminalName(terminalName)
          : ''
      }.`,
    },
    [InvoiceStatus.TerminalProcessRetry]: {
      borderColor: '#da4167',
      backgroundColor: '#fbeaee',
      actionButtonText: 'Yes, retry',
      icon: <RedSquareCancelIcon />,
      title: 'Terminal Error',
      message: `We were unable to send the invoice to ${formatTerminalName(
        terminalName,
      )} due to connection issues, please ensure the terminal is connected to the internet. Would you like to retry?`,
    },
    [InvoiceStatus.Error]: {
      borderColor: '#da4167',
      backgroundColor: '#fbeaee',
      actionButtonText: OKGotIt,
      icon: <RedSquareCancelIcon />,
      title: 'Invoice Error',
      message: errorMessage || 'An error occurred processing your transaction. Please try again.',
    },
    [InvoiceStatus.Received]: {
      borderColor: '#5cc098',
      backgroundColor: '#deefe8',
      actionButtonText: OKGotIt,
      icon: <GreenCircleCheckIcon />,
      title: 'Invoice Received',
      message: `You just received a payment from ${clientFirstName} ${clientLastName} for $${invoiceTotal}${
        ' at ' + formatTerminalName(terminalName)
      }.`,
    },
    [InvoiceStatus.CardProcessing]: {
      borderColor: '#edc308',
      backgroundColor: '#fcf4d3',
      actionButtonText: 'Cancel Invoice',
      icon: <YellowCircleInfoIcon />,
      title: 'Processing Payment',
      message: `Your payment is currently being processed.`,
    },
    [InvoiceStatus.CardSuccessful]: {
      borderColor: '#5cc098',
      backgroundColor: '#deefe8',
      actionButtonText: OKGotIt,
      icon: <GreenCircleCheckIcon />,
      title: 'Payment Successful',
      message: `The payment was successfully processed.`,
    },
    [InvoiceStatus.NoTerminalsFound]: {
      borderColor: '#da4167',
      backgroundColor: '#fbeaee',
      actionButtonText: 'Contact Otto Support',
      icon: <RedSquareCancelIcon />,
      title: 'No Terminals Connected',
      message: `For help with terminal setup, please contact our support team.`,
    },
  };

  const { isTablet } = useResolutionProvider();

  if (status === InvoiceStatus.Default) return null;

  const handleTerminalStatusMessage = (): void => {
    switch (status) {
      case InvoiceStatus.TerminalProcessRetry:
        trackEvent(SidePanelInvoicingEvents.TerminalInvoiceRetryProcessing);
        if (retryCallback) retryCallback();
        break;
      case InvoiceStatus.Pending:
        setCurrentInvoiceStatus(InvoiceStatus.Canceled);
        if (cancelCallback) cancelCallback();
        break;
      case InvoiceStatus.NoTerminalsFound:
        dismissCallback();
        break;
      default:
        setCurrentInvoiceStatus(InvoiceStatus.Default);
        dismissCallback();
        break;
    }
  };

  return (
    <InfoContainer
      isTablet={isTablet}
      borderColor={configMap[status].borderColor}
      backgroundColor={configMap[status].backgroundColor}
    >
      <Title data-testid="invoice-status-title">
        {configMap[status].icon} {configMap[status].title}
      </Title>
      <Description data-testid="invoice-status-description">
        <Linkify>{configMap[status].message} </Linkify>
        {status === InvoiceStatus.Pending && (
          <Bold>Please wait for this payment to be fulfilled before you proceed to the next invoice.</Bold>
        )}
      </Description>
      {/*  Don't allow user to interrupt import actions, like a credit card processing or Stripe sending data to the terminal  */}
      {!isTransactionLoading &&
        !(paymentMethodType === StripePaymentMethodType.CardNotPresent && status === InvoiceStatus.CardProcessing) && (
          <ActionButton
            style={{ float: 'left' }}
            tabIndex={0}
            onClick={(): void => handleTerminalStatusMessage()}
            data-testid="invoice-status-message-button"
          >
            {configMap[status].actionButtonText}
          </ActionButton>
        )}
      {!isTransactionLoading && status === InvoiceStatus.TerminalProcessRetry && (
        <ActionButton
          style={{ float: 'right' }}
          tabIndex={1}
          onClick={dismissCallback}
          data-testid="invoice-status-message-dismiss-button"
        >
          No, thanks
        </ActionButton>
      )}
    </InfoContainer>
  );
};

export default InvoiceStatusMessage;

const InfoContainer = styled.div<{ borderColor: string; backgroundColor: string; isTablet: boolean }>`
  border: 1px solid ${({ borderColor }): string => borderColor};
  background-color: ${({ backgroundColor }): string => backgroundColor};
  color: #1e2477;
  font-size: 14px;
  padding: 12px 20px;
  position: absolute;
  bottom: 82px;
  right: ${({ isTablet }): string => (isTablet ? '5%' : '15px')};
  width: ${({ isTablet }): string => (isTablet ? '90%' : '304px')};
`;

const Title = styled.div`
  font-size: 16px;
  padding-bottom: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;

  svg {
    margin-right: 8px;
  }
`;

const ActionButton = styled.div`
  text-decoration: underline;
  font-weight: 600;
  margin-top: 12px;

  &:hover {
    cursor: pointer;
  }
`;

const Description = styled.div`
  word-break: break-word;
`;

const Bold = styled.strong`
  font-weight: 600;
  font-style: bold;
`;
